<template>
    <main>
        <div class="grid">
            <div class="col-12">
                <label for="uploaded[]">{{ $t('vesselProtocol.document') }}</label>
                
                <label for="closedDocuments" class="text-red text-2xl flex align-center justify-content-center"  
                    v-if="this.isDocumentationComplete">
                        {{ $t('SchedulingAttachments.closedDocuments') }}
                </label>

                <FileUpload name="uploaded[]" customUpload @uploader="uploaderResponseMethod" :fileLimit="10"
                    :previewWidth="0" :multiple="true" 
                    accept="application/pdf, .doc, .docx, application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    :disabled="!uiPermissions('UploadSchedulingAttachment').isEnabled || this.isDocumentationComplete"
                    v-if="!uiPermissions('UploadSchedulingAttachment').isHidden">
                    <template #header="{ chooseCallback, uploadCallback, clearCallback, files }">
                        <div class="flex flex-wrap justify-content-between align-items-center flex-1 gap-2">
                            <div class="flex">
                                <ResponsiveButton :label="$t('btn.selectDocuments')" icon="pi pi-file"
                                    class="p-button-outlined m-1" @click="chooseCallback()" v-if="!this.isDocumentationComplete" />
                                <ResponsiveButton :label="$t('btn.send')" icon="pi pi-cloud-upload"
                                    class="p-button-outlined m-1 p-button-success" @click="uploadEvent(uploadCallback)"
                                    severity="success" :disabled="!files || files.length === 0" v-if="!this.isDocumentationComplete" />
                                <ResponsiveButton :label="$t('SchedulingAttachments.closeDocuments')" icon="pi pi-lock" 
                                    v-if="uiPermissions('UploadSchedulingAttachment').isEnabled && !this.isDocumentationComplete" 
                                    class="p-button-outlined m-1" @click="onCloseDocuments($event)"
                                     />
                                <ResponsiveButton :label="$t('SchedulingAttachments.openDocuments')" icon="pi pi-unlock" 
                                    v-if="uiPermissions('UploadSchedulingAttachment').isEnabled && this.isDocumentationComplete"
                                    class="p-button-outlined m-1 p-button-danger" @click="onOpenDocuments($event)"
                                     />
                                <ResponsiveButton :label="$t('SchedulingAttachments.sendNotification')" icon="pi pi-send" 
                                    v-if="uiPermissions('UploadSchedulingAttachment').isEnabled"
                                    class="p-button-outlined m-1 p-button-secondary"  @click="onSendNotification($event)"
                                     />
                                
                            </div>
                            <div class="flex">
                                <ResponsiveButton :label="$t('btn.cancelDocuments')" icon="pi pi-times"
                                    class="p-button-outlined m-1 p-button-danger ms-auto" @click="clearCallback()"
                                    severity="danger" :disabled="!files || files.length === 0" v-if="!this.isDocumentationComplete" />
                            </div>
                        </div>
                    </template>
                    <template #content="{ files, uploadedFiles, removeUploadedFileCallback, removeFileCallback } "  v-if="!this.isDocumentationComplete"> 
                        <div v-if="files.length > 0">
                            <h5>{{ $t('readyToSend') }}</h5>
                            <div class="flex flex-wrap p-0 sm:p-5 gap-5">
                                <div v-for="(file, index) of files" :key="file.name + file.type + file.size"
                                    class="card m-0 px-6 flex flex-column border-1 surface-border align-items-center gap-3">
                                    <span class="font-semibold">{{ file.name }}</span>
                                    <div>{{ formatSize(file.size) }}</div>
                                    <Badge :value="$t('readyToSend')" severity="warning" class="mt-2" />
                                    <Button icon="pi pi-times" class="mt-2 p-button-danger"
                                        @click="onRemoveTemplatingFile(file, removeFileCallback, index)" outlined
                                        rounded severity="danger" />
                                </div>
                            </div>
                        </div>
                        <div v-if="uploadedFiles.length > 0">
                            <h5>{{ $t('Imported') }}</h5>
                            <div class="flex flex-wrap p-0 sm:p-5 gap-5">
                                <div v-for="(file, index) of uploadedFiles" :key="file.name + file.type + file.size"
                                    class="card m-0 px-6 flex flex-column border-1 surface-border align-items-center gap-3">
                                    <div>
                                        <img role="presentation" :alt="file.name" :src="file.objectURL" width="100"
                                            height="50" class="shadow-2" />
                                    </div>
                                    <span class="font-semibold">{{ file.name }}</span>
                                    <div>{{ formatSize(file.size) }}</div>
                                    <Badge :value="$t('Imported')" class="mt-3" severity="success" />
                                    <Button icon="pi pi-times" @click="removeUploadedFileCallback(index)" outlined
                                        rounded severity="danger" />
                                </div>
                            </div>
                        </div>
                    </template>
                    <template #empty  v-if="!this.isDocumentationComplete">
                        <div class="flex align-items-center justify-content-center flex-column">
                            <i class="pi pi-cloud-upload border-2 border-circle p-5 text-8xl text-400 border-400" />
                            <p class="mt-4 mb-0">{{ $t('vesselProtocol.dragAndDropFile') }}</p>
                        </div>
                    </template>
                </FileUpload>
            </div>
            <div class="col-12 text-right pr-5">
                <div >
                    <ResponsiveButton :label="$t('SchedulingAttachments.downloadDocuments')" 
                        icon="pi pi-download" v-tooltip="$t('btn.download')" v-if="!attachments || attachments.length !== 0"
                        class="text-right p-button-rounded p-button-outlined p-button-success" @click="downloadZipDocuments()"
                    />
                </div>
                
            </div>
            <div class="col-12">
                <DataTable :value="attachments" sortField="documentDate" :sortOrder="-1">
                    <Column>
                        <template #body="slotProps">
                            <ResponsiveButton icon="pi pi-download" v-tooltip="$t('btn.download')"
                                class="p-button-rounded p-button-outlined" @click="downloadDocument(slotProps.data)"
                                v-if="slotProps.data.documentGuid" />
                            <ResponsiveButton icon="pi pi-external-link" v-tooltip="$t('btn.viewFile')"
                                class="p-button-rounded p-button-outlined"
                                @click="openDocument(slotProps.data.documentUrl)" v-else />
                        </template>
                    </Column>
                    <Column field="fileName" :header="$t('vesselProtocol.fileName')" :sortable="true" />
                    <Column field="documentType" :header="$t('vesselProtocol.documentType')" :sortable="true" />
                    <Column field="description" :header="$t('vesselProtocol.description')" :sortable="true" />
                    <Column field="fileExtension" :header="$t('vesselProtocol.extension')" :sortable="true">
                        <template #body="element">
                            {{ element.data.fileExtension ? element.data.fileExtension.split('.').at(-1).toUpperCase() :
                                "" }}
                        </template>
                    </Column>
                    <Column field="customer" :header="$t('vesselProtocol.customer')" :sortable="true" />
                    <Column field="createdBy" :header="$t('vesselProtocol.createdBy')" :sortable="true" />
                    <Column field="documentDate" :header="$t('documentDate')" :sortable="true">
                        <template #body="element">
                            {{ element.data.documentDate ?
                                utils.formatDateTime(utils.dateToCurrentTimeZone(element.data.documentDate)) : null }}
                        </template>
                    </Column>
                    <Column>
                        <template #body="slotProps">
                            <ResponsiveButton icon="pi pi-trash" v-tooltip="$t('btn.delete')"
                                class="p-button-rounded p-button-outlined p-button-danger mr-2"
                                @click="deleteDocument(slotProps.data)" v-if="!this.isDocumentationComplete"
                                :disabled="!uiPermissions('UploadSchedulingAttachment').isEnabled" />
                        </template>
                    </Column>
                </DataTable>
            </div>
        </div>
    </main>
</template>

<script>
import Service from '@/services/vessel-protocol';
import CommonConstants from '@/common/Constants';
import Utils from '@/common/Utils';
import OperativePlanService from '../../services/operative-plan';


export default {
    props: ['id'],
    data() {
        return {
            tempId: null,
            vesselProtocolId: null,
            attachments: [],
            newDocument: {
                type: 'Attuazione',
                category: 'Altro'
            },
            isDocumentationComplete :false,
            voyageNumber: null,
        }
    },
    mounted() {
        this.loadVesselProtocol(this.id);
        this.loadScheduletOperationProtocol(this.id);

    },
    methods: {
        loadVesselProtocol(id){
            Service.getVesselProtocolByLotId({ id: id }, ({ success, data, message }) => {
                this.$store.dispatch("generic/setLoading", false);
                if (success) {
                    this.vesselProtocolId = data.id;
                    this.voyageNumber = data.voyageNumber;
                    this.loadDocuments();
                } else {
                    if(message == 'LOT_IS_NOT_MAIN'){
                        this.loadVesselProtocol(data.id);
                    }else{
                        this.$toast.add({ detail: message && message != '' ? message : this.$t('messages.generic_error_message'), life: 3000, severity: 'error' });
                    }
                }
            })
        },
        loadScheduletOperationProtocol(id){
            OperativePlanService.getScheduledOperationByLotId(id, ({ success, data, message }) => {
                this.$store.dispatch("generic/setLoading", false);
                if (success) {
                    this.isDocumentationComplete = data.isDocumentationComplete;
                } else {
                    
                    this.$toast.add({ detail: message && message != '' ? message : this.$t('messages.generic_error_message'), life: 3000, severity: 'error' });
                }
            })
        },
        loadDocuments() {
            this.$store.dispatch("generic/setLoading", true);
            Service.getAttachmentsSchedulingByIdVesselProtocol(this.vesselProtocolId, ({ success, data, message }) => {
                this.$store.dispatch("generic/setLoading", false);
                if (success) {
                    this.attachments = data;
                } else {
                    this.data = [];
                    this.$toast.add({ detail: message || this.$t('messages.generic_error_message'), life: 3000, severity: 'error' });
                }
            });
        },
        downloadDocument({ documentGuid, fileName, fileExtension }) {
            this.$store.dispatch("generic/setLoading", true);
            let fileNameWithExtension = fileName + fileExtension;
            Service.downloadDocumentVesselProtocol({ documentGuid: documentGuid, fileNameWithExtension }, ({ success, data, message }) => {
                this.$store.dispatch("generic/setLoading", false);
                if (success) {
                    var fileURL = window.URL.createObjectURL(new Blob([data]));
                    var fileLink = document.createElement('a');
                    fileLink.href = fileURL;
                    fileLink.setAttribute('download', fileNameWithExtension);
                    document.body.appendChild(fileLink);
                    fileLink.click();
                } else {
                    this.$toast.add({ detail: message || this.$t('messages.generic_error_message'), life: 3000, severity: 'error' });
                }
            })
        },
        onRemoveTemplatingFile(file, removeFileCallback, index) {
            removeFileCallback(index);
            this.totalSize -= parseInt(this.formatSize(file.size));
            this.totalSizePercent = this.totalSize / 10;
        },
        onClearTemplatingUpload(clear) {
            clear();
            this.totalSize = 0;
            this.totalSizePercent = 0;
        },
        onSelectedFiles(event) {
            this.files = event.files;
            this.files.forEach((file) => {
                this.totalSize += parseInt(this.formatSize(file.size));
            });
        },
        uploadEvent(callback) {
            this.totalSizePercent = this.totalSize / 10;
            callback();
        },
        uploaderResponseMethod(file) {
            const toBase64 = file => new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => resolve(reader.result.split(',')[1]);
                reader.onerror = reject;
            });
            file.files.forEach(async (document) => {
                const base64 = await toBase64(document);

                let section = this.newDocument.type;

                let formData = {
                    idVesselProtocol: this.vesselProtocolId,
                    section: section,
                    description: this.newDocument.description,
                    documentType: this.newDocument.category,
                    fileNameWithExtension: document.name,
                    fileContentBase64: base64,
                }
                this.$store.dispatch("generic/setLoading", true);

                this.lastLoaded = null;
                Service.addDocumentVesselProtocolDocumentManager(formData, ({ success, data, message }) => {
                    this.$store.dispatch("generic/setLoading", false);
                   
                    if (success) {
                        this.$toast.add({ detail: this.$t('messages.upload_message_success'), life: 3000, severity: 'success' });
                        this.loadDocuments();
                    } else {
                        this.$toast.add({ detail: message && message != '' ? message : this.$t('messages.generic_error_message'), life: 3000, severity: 'error' });
                    }
                })
            })
            this.loadDocuments();
        },
        formatSize(bytes) {
            if (bytes === 0) {
                return '0 B';
            }

            let k = 1000,
                dm = 3,
                sizes = ['Byte', 'KiloByte', 'MegaByte', 'GigaByte', 'TeraByte', 'PetaByte', 'EByte', 'ZByte', 'YByte'],
                i = Math.floor(Math.log(bytes) / Math.log(k));

            return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
        },
        deleteDocument(item) {
            this.$confirm.require({
                message: this.$t('messages.delete_message_question'),
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: this.$t('yes'),
                rejectLabel: this.$t('no'),
                accept: () => {
                    let docData = {
                        idVesselProtocol: this.vesselProtocolId,
                        documentGuid: item.documentGuid
                    };

                    Service.deleteAttachmentVesselProtocolDocumentManager(docData, ({ success, message }) => {
                        if (success) {
                            this.loadDocuments();
                            this.$toast.add({ severity: 'success', summary: 'Success Message', detail: this.$t('messages.delete_message_success'), life: 3000 });
                        } else {
                            this.$toast.add({ detail: message || this.$t('messages.generic_error_message'), life: 3000, severity: 'error' });
                        }
                    })
                },
                reject: () => {
                    this.$confirm.close();
                }
            });
        },
        onCloseDocuments(event){
            this.$confirm.require({
                target: event.currentTarget,
                message: this.$t('SchedulingAttachments.messages.close_documents_messages'),
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: this.$t('yes'),
                rejectLabel: this.$t('no'),
                accept: () => {
                    let payload ={id: this.voyageNumber};
                    OperativePlanService.closeVoyageDocumentationComplete( payload,  
                        response => {
                            if(response.success){
                                this.loadScheduletOperationProtocol(this.id);
                                this.$toast.add({severity:'success', summary: 'Success Message', detail: this.$t('messages.operation_message_success'), life: 3000});
                            }
                            else{
                                this.$toast.add({detail: response.message && response.message != '' ? response.message :  this.$t('messages.generic_error_message'), life:3000, severity:'error'});
                            }
                        }
                    )
                    OperativePlanService.sendMailScheduledDocumentation( this.voyageNumber, 
                        response => {
                            if(response.success){
                                this.$toast.add({severity:'success', summary: 'Success Message', detail: this.$t('SchedulingAttachments.messages.send_notification_success'), life: 3000});
                            }
                            else{
                                this.$toast.add({detail: response.message && response.message != '' ? response.message :  this.$t('messages.generic_error_message'), life:3000, severity:'error'});
                            }
                        }
                    )
                    this.$confirm.close();
                },
                reject: () => {
                    //callback to execute when user rejects the action
                    this.$confirm.close();
                }
            });
        },
        onOpenDocuments(event){
            this.$confirm.require({
                target: event.currentTarget,
                message: this.$t('SchedulingAttachments.messages.open_documents_messages'),
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: this.$t('yes'),
                rejectLabel: this.$t('no'),
                accept: () => {
                    let payload ={id: this.voyageNumber};
                    OperativePlanService.reopenVoyageDocumentationComplete(payload, 
                    response => {
                        if(response.success){
                            this.loadScheduletOperationProtocol(this.id);
                            this.$confirm.close();
                            this.$toast.add({severity:'success', summary: 'Success Message', detail: this.$t('messages.operation_message_success'), life: 3000});
                        }
                        else{
                            this.$toast.add({detail: response.message && response.message != '' ? response.message :  this.$t('messages.generic_error_message'), life:3000, severity:'error'});
                        }
                    }
                    )
                },
                reject: () => {
                    //callback to execute when user rejects the action
                    this.$confirm.close();
                }
            });
        },
        onSendNotification(event){
            this.$confirm.require({
                target: event.currentTarget,
                message: this.$t('SchedulingAttachments.messages.send_notification_messages'),
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: this.$t('yes'),
                rejectLabel: this.$t('no'),
                accept: () => {
                    OperativePlanService.sendMailScheduledDocumentation( this.voyageNumber, 
                        response => {
                            if(response.success){
                                this.$confirm.close();
                                this.$toast.add({severity:'success', summary: 'Success Message', detail: this.$t('SchedulingAttachments.messages.send_notification_success'), life: 3000});
                            }
                            else{
                                this.$toast.add({detail: response.message && response.message != '' ? response.message :  this.$t('messages.generic_error_message'), life:3000, severity:'error'});
                            }
                        }
                    )
                },
                reject: () => {
                    //callback to execute when user rejects the action
                    this.$confirm.close();
                }
            });
        },
        downloadZipDocuments() {
            this.$store.dispatch("generic/setLoading", true);
            Service.getVesselProtocolZipDocuments( this.vesselProtocolId, ({ success, data, message }) => {
                this.$store.dispatch("generic/setLoading", false);
                if (success) {
                    var fileURL = window.URL.createObjectURL(new Blob([data]));
                    var fileLink = document.createElement('a');
                    fileLink.href = fileURL;
                    fileLink.setAttribute('download', 'Documents_' + this.voyageNumber + '.zip');
                    document.body.appendChild(fileLink);
                    fileLink.click();
                } else {
                    this.$toast.add({ detail: message || this.$t('messages.generic_error_message'), life: 3000, severity: 'error' });
                }
            })
        },
    },
    computed: {
        constants() {
            return CommonConstants;
        },
        utils() {
            return Utils;
        },
        uiPermissions() {
            return this.$store.getters['security/uiPermissions'];
        }
    }
};
</script>